import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query"
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import useAsclepius from "@common/composables/api/asclepius"
import { QueryApiCacheKeys } from "@common/config/query-api-cache-keys"
import type {
  CreateLegalDependentRequest,
  CreateLegalDependentResponse,
  LegalDependentWithDetails,
  ListActiveLegalDependentsResponse,
  PatchedUpdateLegalDependentRequest,
  TerminateLegalDependentResponse,
  UpdateLegalDependentResponse
} from "@vitable/asclepius-api-client-ts"

export function useListLegalDependents(memberId: string) {
  const { getBenefitsApi } = useAsclepius()

  const { data, isLoading, error, refetch } = useQuery<ListActiveLegalDependentsResponse>({
    queryKey: [QueryApiCacheKeys.LEGAL_DEPENDENTS, memberId],
    queryFn: async () => {
      const response = await getBenefitsApi().listActiveLegalDependents({ memberId })
      return response.data
    },
    enabled: !!memberId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })

  const legalDependents = computed<LegalDependentWithDetails[]>(() => data.value?.data ?? [])

  return {
    legalDependents,
    isLoading,
    error,
    refetch
  }
}

export function useCreateLegalDependent() {
  const { getBenefitsApi, callWithResponse } = useAsclepius()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({
      memberId,
      request
    }: {
      memberId: string
      request: CreateLegalDependentRequest
    }): Promise<CreateLegalDependentResponse> => {
      return await callWithResponse<CreateLegalDependentResponse>(() =>
        getBenefitsApi().createLegalDependent({
          memberId,
          createLegalDependentRequest: request
        })
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryApiCacheKeys.LEGAL_DEPENDENTS] })
    }
  })

  function createLegalDependent(
    memberId: string,
    request: CreateLegalDependentRequest
  ): Promise<CreateLegalDependentResponse> {
    return mutation.mutateAsync({ memberId, request })
  }

  return {
    createLegalDependent,
    isPending: computed(() => mutation.isPending.value),
    error: computed(() => mutation.error.value)
  }
}

export function useUpdateLegalDependent() {
  const { getBenefitsApi, callWithResponse } = useAsclepius()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({
      memberId,
      legalDependentId,
      request
    }: {
      memberId: string
      legalDependentId: string
      request: PatchedUpdateLegalDependentRequest
    }): Promise<UpdateLegalDependentResponse> => {
      return await callWithResponse<UpdateLegalDependentResponse>(() =>
        getBenefitsApi().updateLegalDependent({
          memberId,
          legalDependentId,
          patchedUpdateLegalDependentRequest: request
        })
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryApiCacheKeys.LEGAL_DEPENDENTS] })
    }
  })

  function updateLegalDependent(
    memberId: string,
    legalDependentId: string,
    request: PatchedUpdateLegalDependentRequest
  ): Promise<UpdateLegalDependentResponse> {
    return mutation.mutateAsync({ memberId, legalDependentId, request })
  }

  return {
    updateLegalDependent,
    isPending: computed(() => mutation.isPending.value),
    error: computed(() => mutation.error.value)
  }
}

export function useTerminateLegalDependent() {
  const { getBenefitsApi, callWithResponse } = useAsclepius()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({
      memberId,
      legalDependentId
    }: {
      memberId: string
      legalDependentId: string
    }): Promise<TerminateLegalDependentResponse> => {
      return await callWithResponse<TerminateLegalDependentResponse>(() =>
        getBenefitsApi().terminateLegalDependent({ memberId, legalDependentId })
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryApiCacheKeys.LEGAL_DEPENDENTS] })
    }
  })

  function terminateLegalDependent(
    memberId: string,
    legalDependentId: string
  ): Promise<TerminateLegalDependentResponse> {
    return mutation.mutateAsync({ memberId, legalDependentId })
  }

  return {
    terminateLegalDependent,
    isPending: computed(() => mutation.isPending.value),
    error: computed(() => mutation.error.value)
  }
}

export function useRemoveLegalDependent() {
  const { t } = useI18n()
  const { terminateLegalDependent, isPending } = useTerminateLegalDependent()

  async function removeLegalDependent(
    memberId: string | null | undefined,
    legalDependentId: string | null | undefined
  ): Promise<{ success: boolean; error?: string }> {
    if (!memberId || !legalDependentId) {
      return {
        success: false,
        error: t("Unable to remove this dependent until it has been saved.")
      }
    }

    try {
      await terminateLegalDependent(memberId, legalDependentId)
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : t("Failed to remove dependent. Please try again.")
      return { success: false, error: message }
    }

    return { success: true }
  }

  return { removeLegalDependent, isRemoving: isPending }
}
